import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HelpCircle, MapPin, FileEdit, Navigation } from 'lucide-react';
import { getPlaceholderHelp } from '@/lib/placeholderHelp';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { ReactNode, useState } from 'react';

interface PlaceholderHelpPopupProps {
  placeholderKey: string;
  children: ReactNode;
}

export const PlaceholderHelpPopup = ({ placeholderKey, children }: PlaceholderHelpPopupProps) => {
  const help = getPlaceholderHelp(placeholderKey);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  
  if (!help) return <>{children}</>;
  
  const handleNavigate = () => {
    if (help.navPath) {
      setOpen(false);
      navigate(help.navPath);
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" side="top" align="start">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-start gap-2">
            <HelpCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm font-mono">{placeholderKey}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Waar vind ik dit veld?
              </p>
            </div>
          </div>
          
          {/* Info sections */}
          <div className="space-y-2 text-sm">
            {/* Location */}
            <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-md">
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-xs text-muted-foreground">Locatie</p>
                <p className="font-semibold">{help.location}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{help.path}</p>
              </div>
            </div>
            
            {/* Field */}
            <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-md">
              <FileEdit className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-xs text-muted-foreground">Veld</p>
                <p className="font-semibold">{help.field}</p>
              </div>
            </div>
          </div>
          
          {/* Navigation button */}
          {help.navPath && (
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full mt-2"
              onClick={handleNavigate}
            >
              <Navigation className="h-4 w-4 mr-2" />
              Ga naar {help.location}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
